'use client';

import { c as _c } from "react/compiler-runtime";
import { TopBar } from '@/app/_components/TopBar';
import { DrawerHeader, SideMenu } from '@/app/_components/SideMenu';
import { type User } from '@/app/api/auth/[...nextauth]/types';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import { type ReactNode, useEffect, useReducer } from 'react';
import { AdminPages, GuestPages, SiteManagerPages, UserPages } from '@/app/_components/SideMenuPages';
import { UserRole } from '@/app/_shared/enums';
import { VersionChangelogButton } from '@/app/_components/VersionChangelogButton';
import { type Changelog } from 'resonetics-webapps-modules';
import { signOut } from 'next-auth/react';
const getSideMenuItems = (isMenuOpen: boolean, newSuggestionsCount: number): Map<UserRole, ReactNode> => new Map<UserRole, ReactNode>([[UserRole.ADMIN, <AdminPages key={UserRole.ADMIN} isMenuOpen={isMenuOpen} newSuggestionsCount={newSuggestionsCount} />], [UserRole.USER, <UserPages key={UserRole.USER} isMenuOpen={isMenuOpen} />], [UserRole.SITE_MANAGER, <SiteManagerPages key={UserRole.USER} isMenuOpen={isMenuOpen} />], [UserRole.GUEST, <GuestPages key={UserRole.GUEST} isMenuOpen={isMenuOpen} />]]);
const useDatadogLogs = (user, version) => {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] !== version) {
    t0 = () => {
      if (process.env.NODE_ENV === "development") {
        return;
      }
      const token = process.env.NEXT_PUBLIC_DD_TOKEN;
      const env = process.env.NEXT_PUBLIC_DD_ENV;
      const service = process.env.NEXT_PUBLIC_DD_SERVICE;
      const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
      const site = process.env.NEXT_PUBLIC_DD_SITE as 'datadoghq.com' | 'us3.datadoghq.com' || "datadoghq.com" as const;
      if (!token || !version) {
        return;
      }
      datadogLogs.init({
        clientToken: token,
        site,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ["error", "warn"],
        sessionSampleRate: 100,
        storeContextsAcrossPages: true,
        beforeSend: _temp,
        env,
        service,
        version
      });
      const rumToken = process.env.NEXT_PUBLIC_DD_RUM_TOKEN;
      if (!rumToken || !applicationId) {
        return;
      }
      datadogRum.init({
        applicationId,
        clientToken: rumToken,
        site,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        allowedTracingUrls: [{
          match: window.location.origin,
          propagatorTypes: ["tracecontext"]
        }],
        storeContextsAcrossPages: true,
        defaultPrivacyLevel: "allow",
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        plugins: [reactPlugin({
          router: false
        })],
        service,
        env,
        version
      });
    };
    t1 = [version];
    $[0] = version;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== user.email || $[4] !== user.id || $[5] !== user.lastloginAt || $[6] !== user.name || $[7] !== user.role || $[8] !== user.site.name) {
    t2 = () => {
      if (process.env.NODE_ENV === "development") {
        return;
      }
      datadogLogs.onReady(() => {
        datadogLogs.setUser({
          email: user.email ?? undefined,
          name: user.name ?? undefined,
          id: String(user.id)
        });
        datadogLogs.setUserProperty("role", user.role);
        datadogLogs.setUserProperty("site", user.site.name);
        datadogLogs.setUserProperty("lastLoginAt", user.lastloginAt.toUTCString());
      });
      datadogRum.onReady(() => {
        datadogRum.setUser({
          email: user.email ?? undefined,
          name: user.name ?? undefined,
          id: String(user.id)
        });
        datadogRum.setUserProperty("role", user.role);
        datadogRum.setUserProperty("site", user.site.name);
        datadogRum.setUserProperty("lastLoginAt", user.lastloginAt.toUTCString());
      });
      return _temp2;
    };
    t3 = [user.email, user.id, user.name, user.role, user.site.name, user.lastloginAt];
    $[3] = user.email;
    $[4] = user.id;
    $[5] = user.lastloginAt;
    $[6] = user.name;
    $[7] = user.role;
    $[8] = user.site.name;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
};
const TIME_TO_LOGOUT = 1000 * 60 * 60 * 10; // 10 hours
const useAutoLogout = lastLoginAt => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== lastLoginAt) {
    t0 = () => {
      const interval = setInterval(() => {
        const isValidSession = Date.now() - (lastLoginAt?.getTime() || Date.now()) <= TIME_TO_LOGOUT;
        if (isValidSession) {
          return;
        }
        signOut();
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    };
    t1 = [lastLoginAt];
    $[0] = lastLoginAt;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};
interface AuthenticatedLayoutProps {
  children: ReactNode;
  role: UserRole;
  changelog: Changelog;
  user: User;
  appVersion: string;
  lastLoginAt: Date | null;
  env: 'development' | 'production' | 'test';
  jmtAccess: boolean;
  qmtAccess: boolean;
  newSuggestionsCount: number;
}
export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = t0 => {
  const $ = _c(30);
  const {
    children,
    role,
    changelog,
    user,
    appVersion,
    lastLoginAt,
    env,
    jmtAccess,
    qmtAccess,
    newSuggestionsCount
  } = t0;
  const [isMenuOpen, toggleMenuOpen] = useReducer(_temp3, false);
  useAutoLogout(lastLoginAt);
  useDatadogLogs(user, appVersion);
  let t1;
  if ($[0] !== env || $[1] !== isMenuOpen || $[2] !== jmtAccess || $[3] !== qmtAccess || $[4] !== role) {
    t1 = <TopBar isMenuOpen={isMenuOpen} onToggleMenu={toggleMenuOpen} env={env} currentRole={role} jmtAccess={jmtAccess} qmtAccess={qmtAccess} />;
    $[0] = env;
    $[1] = isMenuOpen;
    $[2] = jmtAccess;
    $[3] = qmtAccess;
    $[4] = role;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== isMenuOpen || $[7] !== newSuggestionsCount || $[8] !== role) {
    t2 = getSideMenuItems(isMenuOpen, newSuggestionsCount).get(role);
    $[6] = isMenuOpen;
    $[7] = newSuggestionsCount;
    $[8] = role;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <Box flex={1}>{t2}</Box>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      p: 1
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== changelog) {
    t5 = <ListItem sx={t4}><VersionChangelogButton changelog={changelog} /></ListItem>;
    $[13] = changelog;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t5) {
    t6 = <Box display="flex" flexDirection="column" minHeight="100%">{t3}{t5}</Box>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== isMenuOpen || $[19] !== t6) {
    t7 = <SideMenu isMenuOpen={isMenuOpen} onToggleMenu={toggleMenuOpen}>{t6}</SideMenu>;
    $[18] = isMenuOpen;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      overflow: "hidden"
    };
    t9 = <DrawerHeader />;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    };
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== children) {
    t11 = <Box component="main" sx={t8}>{t9}<Box sx={t10}>{children}</Box></Box>;
    $[24] = children;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t1 || $[27] !== t11 || $[28] !== t7) {
    t12 = <Box component="div" display="flex">{t1}{t7}{t11}</Box>;
    $[26] = t1;
    $[27] = t11;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
function _temp(log) {
  log.hostname = "Browser";
  return true;
}
function _temp2() {
  datadogLogs.clearUser();
  datadogRum.clearUser();
}
function _temp3(prev) {
  return !prev;
}